import { Component, Output, EventEmitter, Input, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PreventLeadingSpaceDirective } from '@app/shared/components/utils/prevent-leading-space.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { CustomDateAdapter } from '../add-people-form/add-people-form';
import { Spinner } from '@app/shared/components/spinner/spinner'; // Custom spinner component


export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MMMM D, YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'MMMM D, YYYY',
    monthYearA11yLabel: 'MMM Y',
  },
};
export interface Person {
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  dateOfBirth?: string | Date | null;
  jobInfo: string;
  company?: string;
  jobRoleID?: string;
  timezone: string;
  country: string;
  homeTeamID: string;
  actualTeamID?: string;
  managerID?: string;
  accesslevel: any; // Can be string ID or full object
  accesslevelType?: string;
  utype: string;
  address: string;
  isActive: boolean;
  status: string;
  description?: string;
}

@Component({
  selector: 'app-edit-people-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    NgSelectModule,
    MatProgressSpinnerModule,
    PreventLeadingSpaceDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    Spinner
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' }
  ],
  templateUrl: './edit-people-form.html',
  styleUrl: './edit-people-form.scss'
})
export class EditPeopleForm implements OnInit {
  @Output() personUpdated = new EventEmitter<Person>();
  @Output() close = new EventEmitter<void>();
  
  private _person: Person | null = null;
  @Input() 
  set person(value: Person | null) {
    this._person = value;
    if (value) {
      this.loadPersonData();
      this.storeOriginalValues();
    } else {
      this.originalFormValues = null;
    }
  }
  get person(): Person | null {
    return this._person;
  }

  // Loading state
  isLoading = false;

  // Max date for date picker (today - no future dates)
  maxDate = new Date();

  // Form Controls for validation
  firstNameControl = new FormControl('', [Validators.required]);
  lastNameControl = new FormControl('', [Validators.required]);
  emailControl = new FormControl('', [Validators.required, Validators.email, Validators.maxLength(100)]);
  phoneControl = new FormControl('', [
    Validators.pattern(/^\+?[1-9]\d{9,14}$/),
    Validators.minLength(10),
    Validators.maxLength(15)
  ]);
  dobControl = new FormControl<Date | null>(null, [this.validateAge]);
  companyControl = new FormControl<string | null>(null);
  jobTitleControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  jobRoleControl = new FormControl<string | null>(null, [Validators.required]);
  timezoneControl = new FormControl<string | null>(null, [Validators.required]);
  countryControl = new FormControl<string | null>(null);
  homeTeamControl = new FormControl<string | null>(null, [Validators.required]);
  actualTeamControl = new FormControl<string | null>(null, [Validators.required]);
  managerControl = new FormControl<string | null>(null, [Validators.required]);
  accessLevelControl = new FormControl<string | null>(null, [Validators.required]);
  accessLevelTypeControl = new FormControl<string | null>(null, [Validators.required]);
  addressControl = new FormControl('', [Validators.maxLength(250)]);
  isActiveControl = new FormControl(true);
  descriptionControl = new FormControl('', [Validators.maxLength(500)]);

  // Data signals
  jobRoles = signal<any[]>([]);
  homeTeams = signal<any[]>([]);
  managers = signal<any[]>([]);
  companies = signal<any[]>([]);

  // Static options
  timezoneOptions: any[] = [];
  countryOptions: any[] = [
    { name: 'Canada', code: 'CA' },
    { name: 'India', code: 'IN' }
  ];
  accessLevelOptions: any[] = [];

  readonly accessLevelTypeOptions: { label: string; value: string }[] = [
    { label: 'Member', value: 'Member' },
    { label: 'Manager', value: 'Manager' },
  ];

  // Services
  private apiService = inject(ApiService);
  private toastr = inject(ToastrService);

  // Store server error messages for email and phone fields
  private emailServerErrorMessage = '';
  private phoneServerErrorMessage = '';

  // Store original form values for change detection
  private originalFormValues: any = null;

  ngOnInit(): void {
    // Load form data if person exists and not already loaded via setter
    if (this.person && !this.originalFormValues) {
      this.loadPersonData();
      this.storeOriginalValues();
    }
    
    // Load dropdown data in background
    this.loadJobRoles();
    this.loadTeams();
    this.loadManagers();
    this.loadAccessLevels();
    this.loadTimezones();
    this.loadCompanies();

    // Keep Actual Team in sync with Home Team by default,
    // but do NOT override if Actual Team was set to a different value.
    let previousHomeTeamId: string | null = this.homeTeamControl.value ?? null;
    this.homeTeamControl.valueChanges.subscribe((homeTeamId) => {
      const nextHomeTeamId = homeTeamId ?? null;
      const currentActualTeamId = this.actualTeamControl.value ?? null;

      if (!currentActualTeamId || currentActualTeamId === previousHomeTeamId) {
        this.actualTeamControl.setValue(nextHomeTeamId);
      }

      previousHomeTeamId = nextHomeTeamId;
    });
  }

  private loadPersonData(): void {
    if (this.person) {
      // Update form controls with person data
      this.firstNameControl.setValue(this.resolveFirstName(this.person));
      this.lastNameControl.setValue(this.resolveLastName(this.person));
      this.emailControl.setValue(this.person.email);
      this.phoneControl.setValue(this.person.phone || '');
       if (this.person.dateOfBirth) {
        const dobDate = typeof this.person.dateOfBirth === 'string' 
          ? new Date(this.person.dateOfBirth) 
          : this.person.dateOfBirth;
        this.dobControl.setValue(dobDate);
      } else {
        this.dobControl.setValue(null);
      }
      this.companyControl.setValue(this.person.company || null);
      this.jobTitleControl.setValue(this.person.jobInfo || '');
      this.jobRoleControl.setValue(this.person.jobRoleID || null);
      this.timezoneControl.setValue(this.person.timezone || null);
      this.countryControl.setValue(this.getCountryCodeFromName(this.person.country) || null);
      this.homeTeamControl.setValue(this.person.homeTeamID || null);

      // Default Actual Team to Home Team if backend doesn't provide it.
      this.actualTeamControl.setValue(
        this.person.actualTeamID || this.person.homeTeamID || null
      );

      this.managerControl.setValue(this.person.managerID || null);
      
      // Handle access level - it can be an object or string ID
      const accessLevelId = typeof this.person.accesslevel === 'string' 
        ? this.person.accesslevel 
        : this.person.accesslevel?._id;
      this.accessLevelControl.setValue(accessLevelId || null);
      this.accessLevelTypeControl.setValue(this.person.accesslevelType || null);

      this.addressControl.setValue(this.person.address || '');
      this.isActiveControl.setValue(this.person.isActive !== false);
      this.descriptionControl.setValue(this.person.description || '');
    }
  }

  onClose(): void {
    this.close.emit();
  }

  toggleActiveStatus(): void {
    this.isActiveControl.setValue(!this.isActiveControl.value);
  }

  isFormValid(): boolean {
    return this.firstNameControl.valid &&
           this.lastNameControl.valid &&
           this.emailControl.valid && 
           this.phoneControl.valid && 
           this.dobControl.valid && 
           this.jobTitleControl.valid && 
           this.jobRoleControl.valid &&
           this.timezoneControl.valid && 
           this.homeTeamControl.valid && 
           this.actualTeamControl.valid &&
           this.managerControl.valid &&
           this.accessLevelControl.valid &&
           this.accessLevelTypeControl.valid;
  }

  hasChanges(): boolean {
    if (!this.originalFormValues || !this.person) {
      return false;
    }

    // Normalize phone number for comparison
    const normalizePhone = (phone: any): string => {
      if (!phone) return '';
      return String(phone).trim();
    };

    // Normalize date for comparison
    const normalizeDate = (date: Date | null | undefined): string | null => {
      if (!date) return null;
      if (date instanceof Date) {
        if (isNaN(date.getTime())) return null;
        return date.toISOString().split('T')[0];
      }
      return null;
    };

    // Compare current form values with original values
    const firstName = (this.firstNameControl.value || '').trim();
    const lastName = (this.lastNameControl.value || '').trim();

    const currentValues = {
      firstName,
      lastName,
      name: `${firstName} ${lastName}`.trim(),
      email: (this.emailControl.value || '').trim(),
      phone: normalizePhone(this.phoneControl.value),
      dateOfBirth: normalizeDate(this.dobControl.value),
      jobInfo: (this.jobTitleControl.value || '').trim(),
      company: this.companyControl.value ? String(this.companyControl.value).trim() : null,
      jobRoleID: this.jobRoleControl.value ? String(this.jobRoleControl.value) : null,
      timezone: this.timezoneControl.value ? String(this.timezoneControl.value) : null,
      country: this.getCountryName(this.countryControl.value || '') || null,
      homeTeamID: this.homeTeamControl.value ? String(this.homeTeamControl.value) : null,
        actualTeamID: this.actualTeamControl.value ? String(this.actualTeamControl.value) : null,
      managerID: this.managerControl.value ? String(this.managerControl.value) : null,
      accesslevel: this.accessLevelControl.value ? String(this.accessLevelControl.value) : null,
      accesslevelType: this.accessLevelTypeControl.value ? String(this.accessLevelTypeControl.value) : null,
      address: (this.addressControl.value || '').trim(),
      isActive: this.isActiveControl.value || false,
      description: (this.descriptionControl.value || '').trim()
    };

    // Compare all fields
    return (
      currentValues.firstName !== this.originalFormValues.firstName ||
      currentValues.lastName !== this.originalFormValues.lastName ||
      currentValues.email !== this.originalFormValues.email ||
      currentValues.phone !== this.originalFormValues.phone ||
      currentValues.dateOfBirth !== this.originalFormValues.dateOfBirth ||
      currentValues.jobInfo !== this.originalFormValues.jobInfo ||
      currentValues.company !== this.originalFormValues.company ||
      currentValues.jobRoleID !== this.originalFormValues.jobRoleID ||
      currentValues.timezone !== this.originalFormValues.timezone ||
      currentValues.country !== this.originalFormValues.country ||
      currentValues.homeTeamID !== this.originalFormValues.homeTeamID ||
      currentValues.actualTeamID !== this.originalFormValues.actualTeamID ||
      currentValues.managerID !== this.originalFormValues.managerID ||
      String(currentValues.accesslevel || '') !== String(this.originalFormValues.accesslevel || '') ||
      String(currentValues.accesslevelType || '') !== String(this.originalFormValues.accesslevelType || '') ||
      currentValues.address !== this.originalFormValues.address ||
      currentValues.isActive !== this.originalFormValues.isActive ||
      currentValues.description !== this.originalFormValues.description
    );
  }

  private storeOriginalValues(): void {
    if (!this.person) return;

    // Normalize phone number
    const normalizePhone = (phone: any): string => {
      if (!phone) return '';
      return String(phone).trim();
    };

    // Normalize date for comparison
    const normalizeDate = (date: string | Date | null | undefined): string | null => {
      if (!date) return null;
      if (typeof date === 'string') {
        const d = new Date(date);
        if (isNaN(d.getTime())) return null;
        return d.toISOString().split('T')[0];
      }
      if (date instanceof Date) {
        if (isNaN(date.getTime())) return null;
        return date.toISOString().split('T')[0];
      }
      return null;
    };

    const firstName = this.resolveFirstName(this.person);
    const lastName = this.resolveLastName(this.person);

    this.originalFormValues = {
      firstName,
      lastName,
      name: `${firstName} ${lastName}`.trim(),
      email: (this.person.email || '').trim(),
      phone: normalizePhone(this.person.phone),
      dateOfBirth: normalizeDate(this.person.dateOfBirth),
      jobInfo: (this.person.jobInfo || '').trim(),
      company: this.person.company ? String(this.person.company).trim() : null,
      jobRoleID: this.person.jobRoleID ? String(this.person.jobRoleID) : null,
      timezone: this.person.timezone ? String(this.person.timezone) : null,
      country: this.person.country ? String(this.person.country) : null,
      homeTeamID: this.person.homeTeamID ? String(this.person.homeTeamID) : null,
      actualTeamID: (this.person.actualTeamID ? String(this.person.actualTeamID) : this.person.homeTeamID) || null,
      managerID: this.person.managerID ? String(this.person.managerID) : null,
      accesslevel: typeof this.person.accesslevel === 'string' 
        ? this.person.accesslevel 
        : this.person.accesslevel?._id ? String(this.person.accesslevel._id) : null,
      accesslevelType: this.person.accesslevelType ? String(this.person.accesslevelType) : null,
      address: (this.person.address || '').trim(),
      isActive: this.person.isActive !== false,
      description: (this.person.description || '').trim()
    };
  }

  // Custom validator for age (must be at least 16 years old)
  private validateAge(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Date of birth is optional
    }
    
    const today = new Date();
    const birthDate = new Date(control.value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 16) {
      return { 'minAge': { value: control.value, minAge: 16 } };
    }
    
    if (age > 100) {
      return { 'maxAge': { value: control.value, maxAge: 100 } };
    }
    
    return null;
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid() || this.isLoading || !this.person) return;

    this.isLoading = true;

    try {
      const firstName = (this.firstNameControl.value || '').trim();
      const lastName = (this.lastNameControl.value || '').trim();
      const name = `${firstName} ${lastName}`.trim();

      const updatedPerson: Person = {
        ...this.person,
        firstName,
        lastName,
        name,
        email: this.emailControl.value || '',
        phone: this.phoneControl.value || '',
        dateOfBirth: this.dobControl.value,
        jobInfo: this.jobTitleControl.value || '',
        company: this.companyControl.value || '',
        jobRoleID: this.jobRoleControl.value || '',
        timezone: this.timezoneControl.value || '',
        country: this.getCountryName(this.countryControl.value || ''),
        homeTeamID: this.homeTeamControl.value || '',
        actualTeamID: this.actualTeamControl.value || '',
        managerID: this.managerControl.value || '',
        accesslevel: this.accessLevelControl.value || '',
        accesslevelType: this.accessLevelTypeControl.value || '',
        address: this.addressControl.value || '',
        isActive: this.isActiveControl.value || false,
        description: this.descriptionControl.value || ''
      };

      // Call API to update person
      const result = await firstValueFrom(this.apiService.updatePeople(updatedPerson));
       // Update personData with the response data if available
       const finalUpdatedPerson = {
        ...updatedPerson,
        _id: result.data?._id || updatedPerson._id,
        updatedAt: result.data?.updatedAt || new Date().toISOString(),
        dateOfBirth: result.data?.dateOfBirth || updatedPerson.dateOfBirth
      };
      
      this.personUpdated.emit(finalUpdatedPerson);
      this.toastr.success(result.message || 'Profile updated successfully!');
      this.onClose();
    } catch (error: any) {
      console.error('Error updating person:', error);
      
      // Extract backend error message from HTTP error response
      // Angular HttpClient wraps errors, so check error.error for backend response
      let errorMessage = 'Failed to update person. Please try again.';
      
      if (error?.error?.message) {
        // Backend error message (e.g., "Email already exists.")
        errorMessage = error.error.message;
      } else if (error?.error?.error?.message) {
        // Sometimes nested in error.error.error
        errorMessage = error.error.error.message;
      } else if (error?.message) {
        // Fallback to HTTP error message
        errorMessage = error.message;
      }
      
      // Show backend error message in toastr
      this.toastr.error(errorMessage);
      
      // Highlight email field if error is related to email
      if (errorMessage.toLowerCase().includes('email')) {
        // Set custom error on email control to highlight the field
        this.emailControl.setErrors({ 'serverError': true });
        this.emailControl.markAsTouched();
        this.emailServerErrorMessage = errorMessage;
        
        // Focus the email input field and scroll into view
        setTimeout(() => {
          const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement;
          if (emailInput) {
            emailInput.focus();
            emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      // Highlight phone field if error is related to phone
      if (errorMessage.toLowerCase().includes('phone') || errorMessage.toLowerCase().includes('mobile')) {
        // Store the error message for display
        this.phoneServerErrorMessage = errorMessage;
        
        // Set custom error on phone control to highlight the field
        this.phoneControl.setErrors({ 'serverError': true });
        this.phoneControl.markAsTouched();
        
        // Focus the phone input field and scroll into view
        setTimeout(() => {
          const phoneInput = document.querySelector('input[name="phone"]') as HTMLInputElement;
          if (phoneInput) {
            phoneInput.focus();
            phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      // Modal stays open - don't call onClose() on error
    } finally {
      this.isLoading = false;
    }
  }

  // Get email error message for display
  getEmailErrorMessage(): string {
    return this.emailServerErrorMessage || 'Email already exists. Please use a different email address.';
  }

  // Get phone error message for display
  getPhoneErrorMessage(): string {
    return this.phoneServerErrorMessage || 'Phone number is invalid. Please use a different phone number.';
  }

  private loadJobRoles(): void {
    this.apiService.getJobRole().subscribe({
      next: (res: any) => {
        const jobRoles = res.data || res;
        this.jobRoles.set(Array.isArray(jobRoles) ? jobRoles : []);
      },
      error: (error) => {
        console.error('Error loading job roles:', error);
        this.jobRoles.set([]);
      }
    });
  }

  private loadTeams(): void {
    this.apiService.getHomeTeamList().subscribe({
      next: (res: any) => {
        const teams = res.data || res;
         const teamsArray = Array.isArray(teams) ? teams : [];
        const mappedTeams = teamsArray.map((team: any) => ({
          ...team,
          teamName: team.tname || team.teamName || team.name || ''
        }));
        this.homeTeams.set(mappedTeams);
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.homeTeams.set([]);
      }
    });
  }

  private loadAccessLevels(): void {
    this.apiService.getAccessLevelList().subscribe({
      next: (res: any) => {
        const accessLevels = res.data || res;
        this.accessLevelOptions = Array.isArray(accessLevels) ? accessLevels : [];
      },
      error: (error) => {
        console.error('Error loading access levels:', error);
        this.accessLevelOptions = [];
      }
    });
  }
  
  private loadTimezones(): void {
    this.apiService.getTimeZoneList().subscribe({
      next: (res: any) => {
        const timezones = res.data || res;
        this.timezoneOptions = Array.isArray(timezones) ? timezones : [];
      },
      error: (error) => { 
        console.error('Error loading timezones:', error);
        this.timezoneOptions = [];
      }
    });
  }

  private loadManagers(): void {
    this.apiService.getManagerData().subscribe({
      next: (res: any) => {
        const users = res.data || res;
        // Filter users who can be managers (you might want to add a role check here)
        this.managers.set(Array.isArray(users) ? users : []);
      },
      error: (error) => {
        console.error('Error loading managers:', error);
        this.managers.set([]);
      }
    });
  }
  
  private loadCompanies(): void {
    this.apiService.getCompanyList().subscribe({
      next: (res: any) => {
        const companies = res.data || res;
        this.companies.set(Array.isArray(companies) ? companies : []);
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.companies.set([]);
      }
    });
  }

  private getCountryName(countryCode: string): string {
    const country = this.countryOptions.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  }

  private getCountryCodeFromName(countryName: string): string {
    const country = this.countryOptions.find(c => c.name === countryName);
    return country ? country.code : countryName;
  }

  private resolveFirstName(person: Person | Record<string, unknown>): string {
    const p = person as Person;
    if (p.firstName?.trim()) {
      return p.firstName.trim();
    }
    const parts = (p.name || '').trim().split(/\s+/).filter(Boolean);
    return parts[0] || '';
  }

  private resolveLastName(person: Person | Record<string, unknown>): string {
    const p = person as Person;
    if (p.lastName?.trim()) {
      return p.lastName.trim();
    }
    const parts = (p.name || '').trim().split(/\s+/).filter(Boolean);
    return parts.slice(1).join(' ');
  }
}
